import React, { Fragment, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Delete from "@mui/icons-material/Delete";
import TextInput from "@components/inputs/TextInput/TextInput";
import MediaSelector from "@components/inputs/MediaSelector/MediaSelector";
import Card from "@components/cards/Card/Card";
import Button from "@components/buttons/Button/Button";
import IconButton from "@components/buttons/IconButton/IconButton";
import DeleteConfirmationPopup from "@components/modals/DeleteConfirmationPopup/DeleteConfirmationPopup";
import {
    MAX_MEMORIAL_GALLERY_MEDIA,
    MAX_MEMORIAL_GALLERY_SECTIONS,
    MAX_MEMORIAL_GALLERY_SECTION_IMAGE_SIZE,
    MAX_MEMORIAL_GALLERY_SECTION_NAME_LENGTH,
    MAX_MEMORIAL_GALLERY_SECTION_VIDEO_SIZE
} from "@constants/validation";
import { isValidMaxLength } from "@util/validators";
import {
    GalleryMediaAdd,
    GalleryMediaChange,
    GalleryMediaDelete,
    GallerySectionAdd,
    GallerySectionChange
} from "@models/Gallery";
import { MediaFile } from "@models/Common";
import styles from "./GalleryForm.module.css";

export interface GalleryFormSection {
    id: string;
    title?: string;
    media: (GalleryMediaChange | null)[];
}

export interface GallerySectionWithError extends GalleryFormSection {
    hasError: boolean;
    mediaError: string;
}

interface GalleryFormProps {
    state: GallerySectionWithError[];
    onChange: (galleryEntries: GallerySectionWithError[]) => void;
    onSectionAdd: (newSection: GallerySectionAdd) => void;
    onSectionChange: (updatedSection: GallerySectionChange) => void;
    onSectionDelete: (id: string) => void;
    onMediaAdd: (newMedia: GalleryMediaAdd) => void;
    onMediaDelete: (deletedMedia: GalleryMediaDelete) => void;
}

type ConfirmMode = "section" | "media";

const validateSectionName = (val: string) =>
    isValidMaxLength(val, MAX_MEMORIAL_GALLERY_SECTION_NAME_LENGTH);

/* ===========================
   SECTION CARD
=========================== */

type SectionCardProps = {
    numSections: number;
    section: GallerySectionWithError;
    sectionIndex: number;
    mediaCount: number;
    moveEntryUp: (index: number) => void;
    moveEntryDown: (index: number) => void;
    addMedia: (sectionIndex: number, file: MediaFile) => void;
    setSectionDeleteIndex: (index: number) => void;
    setMediaDeleteIndex: (index: number) => void;
    setConfirmMode: (mode: ConfirmMode) => void;
    setShowConfirmPopup: (val: boolean) => void;
    updateEntry: (index: number, updatedData: Partial<GallerySectionWithError>) => void;
};

const SectionCard: React.FC<SectionCardProps> = ({
    numSections,
    section,
    sectionIndex,
    mediaCount,
    moveEntryUp,
    moveEntryDown,
    addMedia,
    setSectionDeleteIndex,
    setMediaDeleteIndex,
    setConfirmMode,
    setShowConfirmPopup,
    updateEntry,
}) => {
    const [showAddMediaButton, setShowAddMediaButton] = useState(true);

    return (
        <Card className={styles.galleryFormCard}>
            <div className={styles.galleryHeader}>
                <div className={styles.controls}>
                    {sectionIndex > 0 && (
                        <IconButton onClick={() => moveEntryUp(sectionIndex)}>
                            ▲
                        </IconButton>
                    )}
                    {sectionIndex < numSections - 1 && (
                        <IconButton onClick={() => moveEntryDown(sectionIndex)}>
                            ▼
                        </IconButton>
                    )}
                </div>

                <Button
                    variant="secondary"
                    onClick={() => {
                        setSectionDeleteIndex(sectionIndex);
                        setConfirmMode("section");
                        setShowConfirmPopup(true);
                    }}
                >
                    Remove
                </Button>
            </div>

            <TextInput
                label="Section Name"
                id={`section-name-${sectionIndex}`}
                value={section.title || ""}
                onChange={(val) =>
                    updateEntry(sectionIndex, { title: val })
                }
                placeholder="Enter section name..."
                errorMessage={validateSectionName(section.title || "")}
            />

            {/* Existing Media */}
            {section.media.map((mediaItem, mediaIndex) => (
                <div key={mediaItem?._id} className={styles.mediaContainer}>
                    <MediaSelector
                        id={`media-${sectionIndex}-${mediaIndex}`}
                        media={mediaItem?.media || null}
                        maxSizeImage={MAX_MEMORIAL_GALLERY_SECTION_IMAGE_SIZE}
                        maxSizeVideo={MAX_MEMORIAL_GALLERY_SECTION_VIDEO_SIZE}
                        hideChooseButtonOnSelect
                        onMediaChange={() => {}}
                    />

                    <div className={styles.mediaDeleteButtonContainer}>
                        <Button
                            variant="text"
                            onClick={() => {
                                setSectionDeleteIndex(sectionIndex);
                                setMediaDeleteIndex(mediaIndex);
                                setConfirmMode("media");
                                setShowConfirmPopup(true);
                            }}
                        >
                            <Delete fontSize="medium" />
                        </Button>
                    </div>
                </div>
            ))}

            {/* Add Media Button */}
            {showAddMediaButton && (
                <div className={styles.mediaButtonContainer}>
                    <Button onClick={() => setShowAddMediaButton(false)}>
                        Add Media
                    </Button>
                </div>
            )}

            {!showAddMediaButton && (
                <MediaSelector
                    id={`media-new-${sectionIndex}`}
                    media={null}
                    multiple
                    maxSizeImage={MAX_MEMORIAL_GALLERY_SECTION_IMAGE_SIZE}
                    maxSizeVideo={MAX_MEMORIAL_GALLERY_SECTION_VIDEO_SIZE}
                    onMediaChange={(files, error) => {
                        if (error) {
                            alert(error);
                            return;
                        }

                        if (!files) return;

                        const fileArray = Array.isArray(files)
                            ? files
                            : [files];

                        fileArray.forEach((file) => {
                            if (
                                mediaCount <
                                MAX_MEMORIAL_GALLERY_MEDIA
                            ) {
                                addMedia(sectionIndex, file);
                            }
                        });

                        setShowAddMediaButton(true);
                    }}
                />
            )}
        </Card>
    );
};

/* ===========================
   MAIN FORM
=========================== */

const GalleryForm: React.FC<GalleryFormProps> = ({
    state,
    onChange,
    onSectionAdd,
    onSectionChange,
    onSectionDelete,
    onMediaAdd,
    onMediaDelete,
}) => {
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [sectionDeleteIndex, setSectionDeleteIndex] = useState(0);
    const [mediaDeleteIndex, setMediaDeleteIndex] = useState(0);
    const [confirmMode, setConfirmMode] = useState<ConfirmMode>("section");

    const mediaCount = state.reduce(
        (total, section) =>
            total + section.media.filter((m) => m !== null).length,
        0
    );

    const addEntry = (index: number) => {
        if (state.length >= MAX_MEMORIAL_GALLERY_SECTIONS) return;

        const newEntry: GallerySectionWithError = {
            id: uuidv4(),
            title: "",
            media: [],
            hasError: false,
            mediaError: "",
        };

        const updated = [...state];
        updated.splice(index, 0, newEntry);

        onChange(updated);

        onSectionAdd?.({
            id: newEntry.id,
            index,
            title: newEntry.title,
        });
    };

    const removeEntry = (index: number) => {
        const deleted = state[index];
        const updated = state.filter((_, i) => i !== index);
        onChange(updated);
        onSectionDelete(deleted.id);
    };

    const updateEntry = (
        index: number,
        updatedData: Partial<GallerySectionWithError>
    ) => {
        const updated = [...state];
        const entry = { ...updated[index], ...updatedData };
        entry.hasError = Boolean(validateSectionName(entry.title || ""));
        updated[index] = entry;
        onChange(updated);

        onSectionChange({
            id: entry.id,
            title: entry.title,
        });
    };

    const moveEntryUp = (index: number) => {
        if (index === 0) return;

        const updated = [...state];

        const current = updated[index];
        const previous = updated[index - 1];

        // swap
        updated[index] = previous;
        updated[index - 1] = current;

        onChange(updated);

        // notify parent about new indexes
        onSectionChange({
            id: current.id,
            index: index - 1,
            title: current.title,
        });

        onSectionChange({
            id: previous.id,
            index,
            title: previous.title,
        });
    };

    const moveEntryDown = (index: number) => {
        if (index === state.length - 1) return;

        const updated = [...state];

        const current = updated[index];
        const next = updated[index + 1];

        // swap
        updated[index] = next;
        updated[index + 1] = current;

        onChange(updated);

        onSectionChange({
            id: current.id,
            index: index + 1,
            title: current.title,
        });

        onSectionChange({
            id: next.id,
            index,
            title: next.title,
        });
    };

    const addMedia = (sectionIndex: number, file: MediaFile) => {
        if (mediaCount >= MAX_MEMORIAL_GALLERY_MEDIA) return;

        const updated = [...state];

        const newMedia: GalleryMediaAdd = {
            _id: uuidv4(),
            sectionId: updated[sectionIndex].id,
            media: file,
        };

        updated[sectionIndex].media = [
            ...updated[sectionIndex].media,
            newMedia,
        ];

        onChange(updated);
        onMediaAdd(newMedia);
    };

    const removeMedia = (sectionIndex: number, mediaIndex: number) => {
        const updated = [...state];
        const deleted = updated[sectionIndex].media[mediaIndex];

        if (deleted) {
            onMediaDelete({
                sectionId: updated[sectionIndex].id,
                mediaId: deleted._id,
            });
        }

        updated[sectionIndex].media.splice(mediaIndex, 1);
        onChange(updated);
    };

    const handleDelete = () => {
        if (confirmMode === "section") {
            removeEntry(sectionDeleteIndex);
        } else {
            removeMedia(sectionDeleteIndex, mediaDeleteIndex);
        }
        setShowConfirmPopup(false);
    };

    return (
        <div>
            {/* When empty */}
            {state.length === 0 && (
                <div className={styles.buttonContainer}>
                    <Button onClick={() => addEntry(0)}>
                        Add Section
                    </Button>
                </div>
            )}

            {state.map((entry, sectionIndex) => (
                <Fragment key={entry.id}>
                    <SectionCard
                        numSections={state.length}
                        section={entry}
                        sectionIndex={sectionIndex}
                        mediaCount={mediaCount}
                        moveEntryUp={moveEntryUp}
                        moveEntryDown={moveEntryDown}
                        addMedia={addMedia}
                        setSectionDeleteIndex={setSectionDeleteIndex}
                        setMediaDeleteIndex={setMediaDeleteIndex}
                        setConfirmMode={setConfirmMode}
                        setShowConfirmPopup={setShowConfirmPopup}
                        updateEntry={updateEntry}
                    />

                    {sectionIndex === state.length - 1 &&
                        state.length < MAX_MEMORIAL_GALLERY_SECTIONS && (
                            <div className={styles.buttonContainer}>
                                <Button
                                    onClick={() =>
                                        addEntry(sectionIndex + 1)
                                    }
                                >
                                    Add Section
                                </Button>
                            </div>
                        )}
                </Fragment>
            ))}

            <p className={styles.note}>
                Max {MAX_MEMORIAL_GALLERY_MEDIA} Media
            </p>

            <DeleteConfirmationPopup
                visible={showConfirmPopup}
                onConfirm={handleDelete}
                onCancel={() => setShowConfirmPopup(false)}
            />
        </div>
    );
};

export default GalleryForm;